import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Zap, DollarSign,
  Activity, FolderKanban, CalendarDays,
} from "lucide-react";
import { getProjectUsageList } from "../../services/usageService";
import { formatPid } from "../../utils/formatPid";
import "../../assets/css/ProjectList.css";
import "../../assets/css/Urls.css";

const FEATURE_META = {
  tts:                 { bg: "#eff6ff", color: "#1d4ed8" },
  voice:               { bg: "#f1f5f9", color: "#475569" },
  embed:               { bg: "#fff7ed", color: "#c2410c" },
  chat:                { bg: "#f0fdf4", color: "#166534" },
  image:               { bg: "#fdf4ff", color: "#7e22ce" },
  create_project_docs: { bg: "#fdf4ff", color: "#7e22ce" },
};

const featureMeta = (f = "") => FEATURE_META[f.toLowerCase()] ?? { bg: "#f1f5f9", color: "#334155" };

const formatFeature = (f = "") =>
  f.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (iso) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
  };
};

const formatCost = (n) =>
  n === 0 ? "$0.000000" : `$${Number(n).toFixed(6)}`;

const getPaginationItems = (cur, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (cur <= 4)          return [1, 2, 3, 4, 5, "e1", total];
  if (cur >= total - 3)  return [1, "e0", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "e0", cur - 1, cur, cur + 1, "e1", total];
};

const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, sub, leftAccent }) => (
  <div style={{
    background: "#fff",
    border: "1px solid var(--borderColor)",
    borderLeft: leftAccent ? `3px solid ${iconColor}` : undefined,
    borderRadius: 12,
    padding: "18px 20px",
    display: "flex", alignItems: "center", gap: 16,
    boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)",
    width: "100%",
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12,
      background: iconBg, color: iconColor,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <Icon size={22} />
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)", fontWeight: 500, marginBottom: 2 }}>{label}</p>
      <h5 style={{
        margin: 0, fontWeight: 800, color: "#0f172a", lineHeight: 1.2, fontSize: 20,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{value}</h5>
      {sub && <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--muteColor)" }}>{sub}</p>}
    </div>
  </div>
);

const ITEMS_PER_PAGE = 10;

const UsageHistoryDetails = () => {
  const { id: projectPid } = useParams();

  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [totalCount, setTotalCount]   = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    setError("");
    getProjectUsageList(projectPid, currentPage, ITEMS_PER_PAGE)
      .then((res) => {
        const response = res.data?.response ?? {};
        setItems(response.items ?? []);
        setTotalCount(response.total ?? response.pagination?.total ?? (response.items?.length ?? 0));
      })
      .catch(() => setError("Failed to load usage logs."))
      .finally(() => setLoading(false));
  }, [projectPid, currentPage]);

  const pageTokens = items.reduce((s, r) => s + (r.totalTokens ?? 0), 0);
  const pageCost   = items.reduce((s, r) => s + (r.totalCostUsd ?? 0), 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));
  const startIdx   = (currentPage - 1) * ITEMS_PER_PAGE;

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">

        {/* Stat cards */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard icon={FolderKanban} iconBg="var(--primaryTint)" iconColor="var(--primaryColor)"
              label="Project" value={formatPid(projectPid)} sub={`PID: ${formatPid(projectPid)}`} leftAccent />
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard icon={Activity} iconBg="#f0fdf4" iconColor="#166534"
              label="Total Logs" value={totalCount.toLocaleString()} sub="usage entries" />
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard icon={Zap} iconBg="#fdf4ff" iconColor="#7e22ce"
              label="Tokens (page)" value={pageTokens.toLocaleString()} sub="this page" />
          </div>
          <div className="col-12 col-sm-6 col-xl-3">
            <StatCard icon={DollarSign} iconBg="#f0fdf4" iconColor="#166534"
              label="Cost (page)" value={formatCost(pageCost)} sub="this page" />
          </div>
        </div>

        {error && (
          <div style={{ padding: "12px 16px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 8, fontSize: 13, color: "#dc2626", marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Table card */}
        <section className="url-table-card">

          {/* Card header */}
          <div style={{
            padding: "14px 20px", background: "#fafbfc",
            borderBottom: "1px solid var(--borderColor)",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9,
              background: "var(--primaryTint)", color: "var(--primaryColor)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Activity size={16} />
            </div>
            <div>
              <h6 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Usage Logs</h6>
              <p style={{ margin: 0, fontSize: 11, color: "var(--muteColor)" }}>
                {totalCount} total entr{totalCount !== 1 ? "ies" : "y"} for this project
              </p>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table className="table url-table table-hover align-middle mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th className="text-center" style={{ width: 48 }}>#</th>
                  <th style={{ width: "14%" }}>Feature</th>
                  <th style={{ width: "20%" }}>Model</th>
                  <th style={{ width: "11%" }}>Model Type</th>
                  <th style={{ width: "18%" }}>
                    <span className="d-flex align-items-center gap-1"><CalendarDays size={12} /> Date</span>
                  </th>
                  <th style={{ width: "12%" }}>
                    <span className="d-flex align-items-center gap-1"><Zap size={12} /> Tokens</span>
                  </th>
                  <th style={{ width: "13%" }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-5">Loading...</td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-5">No usage logs found.</td>
                  </tr>
                ) : (
                  items.map((item, idx) => {
                    const meta = featureMeta(item.feature);
                    const { date, time } = item.createdAt ? formatDate(item.createdAt) : { date: "—", time: "" };
                    return (
                      <tr key={item.usagePid}>
                        <td className="url-index text-center">{startIdx + idx + 1}</td>

                        <td style={{ overflow: "hidden" }}>
                          <span style={{
                            display: "inline-block", maxWidth: "100%",
                            fontSize: 11, fontWeight: 700, textTransform: "capitalize",
                            background: meta.bg, color: meta.color,
                            borderRadius: 5, padding: "3px 10px",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {item.feature ? formatFeature(item.feature) : "—"}
                          </span>
                        </td>

                        <td style={{ fontSize: 12, color: "#334155", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.modelName ?? "—"}
                        </td>

                        <td style={{ overflow: "hidden" }}>
                          <span style={{
                            fontSize: 11, fontWeight: 600, textTransform: "capitalize",
                            background: "#f1f5f9", color: "#334155",
                            borderRadius: 4, padding: "3px 9px", display: "inline-block",
                            maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {item.modelType ?? "—"}
                          </span>
                        </td>

                        <td style={{ overflow: "hidden" }}>
                          <div style={{ fontSize: 12, color: "#334155", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{date}</div>
                          <div style={{ fontSize: 11, color: "var(--muteColor)", whiteSpace: "nowrap" }}>{time}</div>
                        </td>

                        <td>
                          <span className="ph-token-badge" style={{ whiteSpace: "nowrap" }}>
                            <Zap size={10} />
                            {(item.totalTokens ?? 0).toLocaleString()}
                          </span>
                        </td>

                        <td style={{ fontSize: 12, fontWeight: 600, color: "#166534", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {formatCost(item.totalCostUsd ?? 0)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!loading && totalCount > 0 && (
            <div className="url-pagination-wrap" style={{ flexWrap: "wrap", gap: 8 }}>
              <p className="url-pagination-summary mb-0">
                Showing {startIdx + 1}–{Math.min(startIdx + ITEMS_PER_PAGE, totalCount)} of {totalCount}
              </p>
              <nav>
                <ul className="url-pagination mb-0">
                  <li>
                    <button className="url-page-btn" disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => p - 1)}>
                      <ChevronLeft size={15} />
                    </button>
                  </li>
                  {getPaginationItems(currentPage, totalPages).map((item, i) => (
                    <li key={i}>
                      {typeof item === "string" ? (
                        <span className="url-page-ellipsis">…</span>
                      ) : (
                        <button
                          className={`url-page-btn${currentPage === item ? " active" : ""}`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )}
                    </li>
                  ))}
                  <li>
                    <button className="url-page-btn" disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => p + 1)}>
                      <ChevronRight size={15} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </section>

      </div>
    </main>
  );
};

export default UsageHistoryDetails;
